import { useEffect, useRef, useState } from 'react';

import closeSvg from '../../../../assets/close.svg';
import { Addon, Adjustment } from './interfaces';
import DropdownField from '../../../../components/DropdownField';

const EditAdjustmentModal: React.FC<{
  adjustmentToEdit: Adjustment;
  setNames: string[];
  onClose: () => void;
  onEditAdjustment: (adjustment: Adjustment) => Promise<void>;
  user: string;
  floorplans: string[];
  itemNames: Addon[];
  existingAdjustments: Adjustment[];
}> = ({ adjustmentToEdit, setNames, onClose, onEditAdjustment, user, floorplans, itemNames, existingAdjustments }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const coreAddons = ['Painting', 'Cleaning', 'Carpet Cleaning', 'Maintenance'];

  const [adjustment, setAdjustment] = useState<Adjustment>({
    ...adjustmentToEdit,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.target.name === 'type' && e.target.value !== adjustment.type) {
      setAdjustment({
        ...adjustment,
        type: e.target.value as 'Core' | 'Addon',
        name: e.target.value === 'Core' ? coreAddons[0] : itemNames[0].rr_addonname,
      });
    } else {
      setAdjustment({
        ...adjustment,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) return;

    setAdjustment({
      ...adjustment,
      value: Number(e.target.value),
    });
  };

  const updateSetname = async (name: string) => {
    setAdjustment({
      ...adjustment,
      setname: name,
    });
  };

  const updateItemname = async (name: string) => {
    setAdjustment({
      ...adjustment,
      name: name,
    });
  };

  const updateFloorplan = async (name: string) => {
    setAdjustment({
      ...adjustment,
      config: name,
    });
  };

  const getAlert = (): string => {
    // Check for duplicate combination: user + setname + market + config + type + parameter[name]
    const isDuplicate = existingAdjustments.some(
      a =>
        a.user === adjustment.user &&
        a.setname === adjustment.setname &&
        a.config === adjustment.config &&
        a.type === adjustment.type &&
        a.name === adjustment.name
    );

    if (isDuplicate) {
      return `The combination of set name, config, type, item name must be unique.`;
    }

    // Check for empty fields
    const hasEmptyFields = Object.values(adjustment).some(val => val === '' || val === null || val === undefined);
    if (hasEmptyFields) {
      return 'Please fill in all required fields';
    }

    return '';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(16, 29, 59, 0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div style={styles.modalContent} ref={modalRef} onClick={event => event.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              fontWeight: '700',
              fontSize: '2.4vh',
            }}
          >
            Editing parameter
          </div>

          <button
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            <img src={closeSvg} alt="logout" style={{ width: '2.4vh', height: '2.4vh' }} />
          </button>
        </div>
        <div style={{ minHeight: '1.6vh' }} />
        <div style={styles.label}>Adjustment set name</div>
        <div style={{ minHeight: '0.8vh' }} />
        <DropdownField
          values={setNames}
          onUpdate={updateSetname}
          initialValue={adjustment.setname}
          allowCustom={true}
          placeholder="Add adjustment set name..."
        />
        <div style={{ minHeight: '1.6vh' }} />
        <div style={styles.label}>Item type</div>
        <div style={{ minHeight: '0.8vh' }} />
        <div
          style={{
            color: '#101D3B',
            fontSize: '1.6vh',
            fontWeight: '700',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'start',
            justifyContent: 'start',
            width: '100%',
            gap: '1.6vh',
          }}
        >
          <div className="radio-container">
            <input
              type="radio"
              id="core"
              value="Core"
              name="type"
              checked={adjustment.type === 'Core'}
              onChange={handleChange}
              className="radio-input"
            />
            <label className="radio-label">Core</label>
          </div>
          <div className="radio-container">
            <input
              type="radio"
              id="addon"
              value="Addon"
              name="type"
              checked={adjustment.type === 'Addon'}
              onChange={handleChange}
              className="radio-input"
            />
            <label className="radio-label">Addon</label>
          </div>
        </div>
        <div style={{ minHeight: '1.6vh' }} />
        <div style={styles.label}>Item name</div>
        <div style={{ minHeight: '0.8vh' }} />
        <DropdownField
          values={adjustment.type === 'Core' ? coreAddons : itemNames.map(item => item.rr_addonname)}
          onUpdate={updateItemname}
          initialValue={adjustment.name}
          allowFilter={false}
          displayValues={adjustment.type === 'Core' ? undefined : itemNames.map(item => item.displaystring)}
        />
        <div style={{ minHeight: '1.6vh' }} />
        <div style={styles.label}>Floorplan configuration</div>
        <div style={{ minHeight: '0.8vh' }} />
        <DropdownField
          values={floorplans}
          onUpdate={updateFloorplan}
          initialValue={adjustment.config}
          allowFilter={false}
        />
        <div style={{ minHeight: '1.6vh' }} />
        <div style={styles.label}>Item value</div>
        <div style={{ minHeight: '0.8vh' }} />
        <input
          placeholder="Enter company name..."
          value={adjustment.value}
          style={{
            padding: '1.6vh',
            fontSize: '1.4vh',
            borderRadius: '1.6vh',
            border: '0.1vh solid rgba(16, 29, 59, 0.2)',
            backgroundColor: '#ffffff',
            color: '#101D3B',
            cursor: 'pointer',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box' as 'border-box',
          }}
          onChange={handleValueChange}
        />
        <div style={{ minHeight: '1.6vh' }} />
        {getAlert() !== '' ? (
          <div
            style={{
              width: '100%',
              minHeight: '5.6vh',
              color: 'rgba(16, 29, 59, 0.4)',
              backgroundColor: 'rgba(16, 29, 59, 0.05)',
              fontSize: '1.6vh',
              fontWeight: '700',
              borderRadius: '1.6vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getAlert()}
          </div>
        ) : (
          <button
            style={{
              width: '100%',
              minHeight: '5.6vh',
              color: 'white',
              backgroundColor: 'rgba(0, 171, 155, 1)',
              fontSize: '1.6vh',
              fontWeight: '700',
              borderRadius: '1.6vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
            }}
            onClick={() => onEditAdjustment(adjustment)}
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  modalContent: {
    backgroundColor: 'white',
    padding: '2.4vh',
    borderRadius: '1.6vh',
    width: '52vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    color: 'rgba(16, 29, 59, 1)',
    maxHeight: 'calc(100vh - 6.4vh)',
    overflowY: 'auto' as 'auto',
  },
  label: {
    fontSize: '1.2vh',
    fontWeight: '400',
    color: 'rgba(16, 29, 59, 0.6)',
  },
  container: {
    width: '100%',
    display: 'flex',
  },
  select: {
    padding: '1.2vh',
    borderRadius: '1.6vh',
    border: '0.1vh solid rgba(16, 29, 59, 0.2)',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as 'border-box',
  },
  input: {
    fontSize: '1.4vh',
    fontWeight: '400',
    border: 'none',
    backgroundColor: '#ffffff',
    color: 'rgba(16, 29, 59, 1)',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
  },
  dropdown: {
    position: 'absolute' as 'absolute',
    padding: '0vh 1.6vh',
    top: 'calc(100% - 0.6vh)', // Position it right below the input
    left: '0',
    width: 'calc(100% - 3.2vh)', //compensate padding
    borderTop: 'none',
    maxHeight: '15vh',
    overflowY: 'auto' as 'auto',
    backgroundColor: '#ffffff',
    zIndex: 1,
    borderRadius: '1.6vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
  },
  option: {
    padding: '0.8vh 0vh',
    fontWeight: '400',
    fontSize: '1.4vh',
    cursor: 'pointer',
    borderBottom: '0.1vh solid #eee',
    color: '#2d3436',
    textAlign: 'left' as 'left',
  },
  scrollbar: {
    // Empty for now due to different browser specification complexity
  },
};

export default EditAdjustmentModal;
